import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { chiama } from '../api/cliente.js';
import type { RigaOrdine } from '../api/ordini.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { data, importo, pastigliaPagamento } from '../formati.js';
import { locale, t } from '../i18n/lingua.js';

/**
 * Gli ordini di un cliente, dentro la sua scheda.
 *
 * Nel v2 per vedere gli ordini di qualcuno si esce dall'anagrafica, si apre
 * l'elenco ordini, si preme RICERCA e si sceglie il cliente da una tendina di
 * milleduecento nomi. È il salto che si fa più spesso — «questo cliente cosa
 * ha in giro?» — e qui è una scheda.
 *
 * Si mostrano gli ultimi venti: chi ne vuole di più va all'elenco completo,
 * già filtrato, dal collegamento in fondo.
 */

const QUANTI = 20;

const COLONNE: Colonna<RigaOrdine>[] = [
  {
    chiave: 'orderNumber',
    titolo: t('Ordine'),
    cella: (r) => (
      <Link className="cell-link strong" to="/sales-orders/$id" params={{ id: String(r.id) }}>
        {r.orderNumber}
      </Link>
    ),
  },
  { chiave: 'orderDate', titolo: t('Data'), cella: (r) => data(r.orderDate) },
  {
    chiave: 'state',
    titolo: t('Stato'),
    cella: (r) => (
      <span className="state-pill">
        <i
          className="state-num"
          style={r.stateColor ? ({ '--state': `#${r.stateColor}` } as never) : undefined}
        >
          {r.stateNumber}
        </i>
        {r.state.replace(/^\d+\.\s*/, '')}
      </span>
    ),
  },
  {
    chiave: 'arrival',
    titolo: t('Arrivo'),
    cella: (r) =>
      r.arrival ? (
        <span className={r.late ? 'when late' : r.arrivalIsEstimate ? 'when eta' : 'when'}>
          {data(r.arrival)}
        </span>
      ) : (
        <span className="dim">—</span>
      ),
  },
  {
    chiave: 'total',
    titolo: t('Totale'),
    allineamento: 'destra',
    cella: (r) => importo(r.total, r.currency),
  },
  {
    chiave: 'paymentStatus',
    titolo: t('Pagamento'),
    cella: (r) => {
      const p = pastigliaPagamento(r.paymentStatus);
      return <span className={`badge ${p.classe}`}>{p.testo}</span>;
    },
  },
];

interface Pagina {
  rows: RigaOrdine[];
  total: number;
}

export function OrdiniDelCliente({ identityId }: { identityId: number }) {
  const elenco = useQuery({
    queryKey: ['ordini-cliente', identityId],
    queryFn: () => chiama<Pagina>(`/api/identities/${identityId}/orders?size=${QUANTI}`),
  });

  const dati = elenco.data;

  return (
    <div className="card" style={{ margin: '14px 20px 24px' }}>
      <div className="card-head">
        <span className="card-title">{t('Ordini')}</span>
        {dati && (
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 12 }}>
            {dati.total === 0
              ? 'nessuno'
              : dati.total <= QUANTI
                ? t('{n} in tutto', { n: dati.total })
                : t('gli ultimi {quanti} di {totale}', { quanti: QUANTI, totale: dati.total.toLocaleString(locale()) })}
          </span>
        )}
      </div>
      <Tabella
        colonne={COLONNE}
        righe={dati?.rows ?? []}
        chiaveRiga={(r) => r.id}
        caricamento={elenco.isFetching}
          errore={elenco.error}
        vuoto={t('Questo cliente non ha ordini.')}
      />
      {dati !== undefined && dati.total > QUANTI && (
        <div className="table-foot">
          <Link
            className="cell-link"
            to="/sales-orders"
            search={{ customerId: identityId } as never}
          >
            {t('Vedi tutti i {n} ordini →', { n: dati.total.toLocaleString(locale()) })}
          </Link>
        </div>
      )}
    </div>
  );
}
